"use client";

import { usePathname, useRouter } from "next/navigation";
import React, { useEffect, useState } from "react";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import IconArrowDown from "@/app/(components)/generics/Icons/IconArrowDown";
import IconArrowUp from "@/app/(components)/generics/Icons/IconArrowUp";
import IconCopy from "@/app/(components)/generics/Icons/IconCopy";
import IconPlus from "@/app/(components)/generics/Icons/IconPlus";
import IconTag from "@/app/(components)/generics/Icons/IconTag";
import PageLoader from "@/app/(components)/generics/loaders/PageLoader/PageLoader";
import Popover from "@/app/(components)/generics/Popover/Popover";
import PopoverContent from "@/app/(components)/generics/Popover/PopoverContent";
import PopoverTrigger from "@/app/(components)/generics/Popover/PopoverTrigger";
import { Tooltip, TooltipPosition, TooltipType } from "@/app/(components)/generics/Tootlip/Tooltip";
import { useCanEditContent } from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { useOfferActions } from "@/app/hooks/useOfferActions";
import { OfferStore } from "@/app/stores/OfferStore";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { PageParams } from "@/app/types/common/DefaultProps";
import { URLs } from "@/app/utils/URLs";
import { UStyles } from "@/app/utils/UStyles";
import { Limits } from "@/app/vars/Limits";
import { ProtectedPaths, Routes } from "@/app/vars/Routes";
import sidebarStyles from "../../Sidebar.module.css";
import styles from "./SidebarOffers.module.css";

type Props = {
  params: PageParams;
  isOpen: boolean;
};

export default function SidebarOffers(props: Props) {
  const T = useT();
  const { params, isOpen } = props;

  const projectId = ProjectStore((state) => state.currentProject?.id);
  const offers = OfferStore((state) => state.offers);
  const currentOfferId = OfferStore((state) => state.currentOfferId);
  const offersProjectId = OfferStore((state) => state.offersProjectId);
  const fetchOffers = OfferStore((state) => state.fetchOffers);
  const setCurrentOfferId = OfferStore((state) => state.setCurrentOfferId);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  const canEdit = useCanEditContent();
  const offerActions = useOfferActions(params);

  useEffect(() => {
    if (projectId) {
      fetchOffers(projectId);
    }
  }, [projectId, fetchOffers]);

  const current = offers.find((offer) => offer.id === currentOfferId);

  if (!projectId || offersProjectId !== projectId) {
    return null;
  }

  const isActive = current
    ? pathname.includes(new URL(Routes.PROTECTED.OFFER(current.uuid)).pathname)
    : false;

  const handleOpen = () => {
    if (!current) {
      offerActions.add();
      return;
    }

    router.push(URLs.pathOf(Routes.PROTECTED.OFFER(current.uuid)));
  };

  const handleSelect = (offerId: number) => {
    setIsDropdownOpen(false);

    if (offerId === currentOfferId) {
      return;
    }

    const next = offers.find((offer) => offer.id === offerId);
    if (!next) {
      return;
    }

    setCurrentOfferId(offerId);
    setIsSwitching(true);

    const target = pathname.includes(ProtectedPaths.OFFER)
      ? URLs.pathOf(Routes.PROTECTED.OFFER(next.uuid))
      : null;

    setTimeout(() => (target ? window.location.assign(target) : window.location.reload()), 100);
  };

  const handleAdd = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDropdownOpen(false);
    offerActions.add();
  };

  const handleDuplicate = () => {
    if (!current) {
      return;
    }

    setIsDropdownOpen(false);
    offerActions.duplicate(current);
  };

  const list = (
    <div className={styles.dropdown}>
      <div className={styles.offerList}>
        {offers.map((offer) => (
          <div
            key={offer.id}
            className={UStyles.classNames(
              styles.dropdownItem,
              offer.id === currentOfferId ? styles.activeOffer : "",
            )}
            onClick={() => handleSelect(offer.id)}
          >
            <span className={styles.dropdownName}>{offer.name}</span>
          </div>
        ))}
      </div>

      {canEdit && offers.length < Limits.MAX_PROJECT_OFFERS && (
        <>
          <div className={styles.dropdownSeparator} />

          {current && (
            <div className={styles.action} onClick={handleDuplicate}>
              <IconCopy size={13} />
              <span>{T(TKey.OFFER_DUPLICATE_TITLE)}</span>
            </div>
          )}

          <Button
            full={true}
            min={true}
            isLink={false}
            type={ButtonType.PRIMARY}
            text={T(TKey.SIDEBAR_OFFER_ADD) as string}
            beforeIcon={<IconPlus size={14} />}
            onClick={handleAdd}
          />
        </>
      )}
    </div>
  );

  if (!isOpen) {
    return (
      <div className={styles.collapsedWrapper}>
        <Tooltip
          position={TooltipPosition.RIGHT}
          gap={Limits.SIDEBAR_TOOLTIP_GAP}
          type={TooltipType.GREEN}
          content={current?.name ?? (T(TKey.SIDEBAR_OFFER_ADD) as string)}
          className={sidebarStyles.fullWidth}
          disabled={isDropdownOpen}
          trigger={
            <div className={styles.collapsedTrigger}>
              <Popover
                isVisible={isDropdownOpen}
                setIsVisible={setIsDropdownOpen}
                openAfterClick={true}
                closeWhenClickOutside={true}
                isPortaled={false}
                className={styles.popoverCollapsed}
              >
                <PopoverTrigger className={styles.collapsedIcon}>
                  <IconTag size={20} />
                </PopoverTrigger>
                <PopoverContent
                  position={{
                    default: {
                      top: "0",
                      left: `calc(100% + ${Limits.SIDEBAR_TOOLTIP_GAP * 2}px)`,
                    },
                  }}
                  className={styles.popoverContent}
                >
                  {list}
                </PopoverContent>
              </Popover>
            </div>
          }
        />
      </div>
    );
  }

  return (
    <>
      {isSwitching && <PageLoader active={true} delay={0} />}

      <div className={styles.wrapper}>
        <div
          className={UStyles.classNames(
            styles.trigger,
            isDropdownOpen || isActive ? styles.triggerOpen : "",
          )}
        >
          <div className={styles.clickArea} onClick={handleOpen}>
            <span className={styles.icon}>
              <IconTag size={20} />
            </span>

            <span className={UStyles.classNames(styles.name, isActive ? styles.nameActive : "")}>
              {current?.name ?? (T(TKey.SIDEBAR_OFFER_ADD) as string)}
            </span>
          </div>

          <Popover
            isVisible={isDropdownOpen}
            setIsVisible={setIsDropdownOpen}
            openAfterClick={true}
            closeWhenClickOutside={true}
            isPortaled={true}
            className={styles.popover}
          >
            <PopoverTrigger
              className={UStyles.classNames(
                styles.arrowContainer,
                isDropdownOpen ? styles.arrowActive : "",
              )}
            >
              {isDropdownOpen ? <IconArrowUp size={21} /> : <IconArrowDown size={21} />}
            </PopoverTrigger>

            <PopoverContent
              position={{ default: { top: "calc(100% + 4px)", left: "0" } }}
              className={styles.popoverContent}
            >
              {list}
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </>
  );
}
